import React, { useEffect, useRef, useState, useMemo } from "react";
import { useColorMode, Button, AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import {
  FaCheck,
  FaUserPlus,
  FaEye,
  FaTimes,
  FaArrowLeft,
  FaPlus,
} from "react-icons/fa";
import { CiEdit } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";
import { getDoc, doc, updateDoc, setDoc, arrayUnion } from "firebase/firestore";
import { db, auth } from "../../firebase/firebase";
import { logToSystem } from "../../utils/systemLogging";
import Loader from "../../components/Loader";
import { IconWrapper } from "../../components/IconWrapper";
import { User } from "./KpiMetricMain";
import MetricForm from "./MetricForm";
import CustomDropdown from "../../components/CustomDropdown";
import { getKpiPermissions } from "../../utils/permissions";
import { showToast } from "../../utils/toastUtils";

interface Metric {
  id: number;
  metric: string;
  description?: string;
  target: string;
  actual: string;
  status: string;
  mainId?: string;
  assignedTo: string[]; // Store user IDs
  createdBy?: string;
}

interface MetricTableProps {
  metrics: Metric[];
  updateMetric: (updatedMetric: Metric) => void;
  deleteMetric: (metricId: number) => void;
  users: User[];
  color: string;
  bg: string;
  organizationId: string;
  setMetrics: React.Dispatch<React.SetStateAction<Metric[]>>;
  isAdmin: boolean;
  currentUserId: string;
}

const MetricTable: React.FC<MetricTableProps> = ({
  metrics,
  updateMetric,
  deleteMetric,
  setMetrics,
  users,
  color,
  bg,
  organizationId,
  isAdmin,
  currentUserId,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const currentUser = useMemo(
    () => users.find((u) => u.id === currentUserId),
    [users, currentUserId]
  );
  const kpiPerms = useMemo(() => getKpiPermissions(currentUser), [currentUser]);

  const [editableId, setEditableId] = useState<number | null>(null);
  const [editMetric, setEditMetric] = useState<Metric | null>(null);
  const [viewingUsers, setViewingUsers] = useState<string[] | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const visibleMetrics = useMemo(() => {
    if (kpiPerms.canViewAll) return metrics;
    return metrics.filter((m) => m.assignedTo.includes(currentUserId));
  }, [metrics, kpiPerms.canViewAll, currentUserId]);

  const filteredMetrics = useMemo(
    () =>
      visibleMetrics.filter(
        (m) =>
          m.metric.toLowerCase().includes(search.toLowerCase()) ||
          (m.description && m.description.toLowerCase().includes(search.toLowerCase())) ||
          String(m.target).toLowerCase().includes(search.toLowerCase()) ||
          String(m.actual).toLowerCase().includes(search.toLowerCase()) ||
          m.status.toLowerCase().includes(search.toLowerCase())
      ),
    [visibleMetrics, search]
  );
  const [showMetricForm, setShowMetricForm] = useState(false);
  const [isPermissionModalOpen, setPermissionModalOpen] = useState(false);
  const permissionCancelRef = useRef<HTMLButtonElement>(null);

  // State for Assigned Users Popup
  const [isAssignedUsersPopupOpen, setIsAssignedUsersPopupOpen] = useState(false);
  const [currentMetricUsers, setCurrentMetricUsers] = useState<{ metricName: string; assignedUsers: User[] } | null>(null);

  // Assign Users manager for edit mode
  const [isManageUsersOpen, setIsManageUsersOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  console.log("metric", metrics);

  useEffect(() => {
    const fetchCommonMetrics = async () => {
      setLoading(true);
      try {
        const commonMetricsRef = doc(
          db,
          `organization/${organizationId}/Metrics/commonKpiMetrics`
        );
        const commonMetricsSnap = await getDoc(commonMetricsRef);

        if (commonMetricsSnap.exists()) {
          const data = commonMetricsSnap.data();
          if (data.metrics) {
            setMetrics(data.metrics);
          }
        }
      } catch (error) {
        console.error("❌ Error fetching KPI Metrics:", error);
      }
      setLoading(false);
    };

    fetchCommonMetrics();
  }, [organizationId]);

  // Pass a function-supporting setter
  const updateSelectedUsers = (
    users: string[] | ((prev: string[]) => string[])
  ) => {
    setSelectedUsers((prev) =>
      typeof users === "function" ? users(prev) : users
    );
  };
  const handleEdit = (metric: Metric) => {
    if (kpiPerms.isViewer && metric.createdBy !== kpiPerms.currentUserId) {
      setPermissionModalOpen(true);
      return;
    }
    console.log("edit", metric);
    setEditableId(metric.id);
    setEditMetric({ ...metric });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (editMetric) {
      setEditMetric((prev): Metric | null => {
        if (!prev) return null;
        return {
          ...prev,
          [e.target.name]: e.target.value,
          mainId: prev.mainId ?? String(prev.id), // ✅ Convert to string
          id: prev.id ?? Date.now(), // ✅ Ensure id is defined
        };
      });
    }
  };

  const handleUserSelection = (userId: string) => {
    if (editMetric) {
      const updatedAssignedTo = editMetric.assignedTo.includes(userId)
        ? editMetric.assignedTo.filter((id) => id !== userId) // Remove user
        : [...editMetric.assignedTo, userId]; // Add user

      setEditMetric({ ...editMetric, assignedTo: updatedAssignedTo });
    }
  };

  const handleUpdate = async () => {
    if (!editMetric) return;

    // Validation: No fields should be empty
    if (!editMetric.metric.trim() || !editMetric.target.trim() || !editMetric.actual.trim()) {
      showToast({ title: "All fields (Metric, Target, Actual) must be filled.", status: "warning" });
      return;
    }

    setIsUpdating(true);
    if (editMetric) {
      try {
        // ✅ 1. Update Firestore: `commonKpiMetrics`
        const commonMetricsRef = doc(
          db,
          `organization/${organizationId}/Metrics/commonKpiMetrics`
        );
        const commonMetricsSnap = await getDoc(commonMetricsRef);

        let oldAssignedUsers: string[] = [];
        if (commonMetricsSnap.exists()) {
          const data = commonMetricsSnap.data();

          // ✅ Find and update the metric in `commonKpiMetrics`
          const updatedMetrics = data.metrics.map((metric: Metric) => {
            if (metric.id === editMetric.id) {
              oldAssignedUsers = metric.assignedTo || [];
              return { ...editMetric };
            }
            return metric;
          });

          await updateDoc(commonMetricsRef, { metrics: updatedMetrics });
        }

        // ✅ 2. Compute Added & Removed Users
        const newAssignedUsers = editMetric.assignedTo;
        const removedUsers = oldAssignedUsers.filter(
          (user) => !newAssignedUsers.includes(user)
        );
        const addedUsers = newAssignedUsers.filter(
          (user) => !oldAssignedUsers.includes(user)
        );

        // ✅ 3. Remove metric from unassigned users in `userKpiMetrics`
        for (const userId of removedUsers) {
          const userKpiRef = doc(
            db,
            `organization/${organizationId}/Metrics/userKpiMetrics`
          );
          const userKpiSnap = await getDoc(userKpiRef);

          if (userKpiSnap.exists()) {
            const userData = userKpiSnap.data();
            if (userData.users && userData.users[userId]) {
              // Remove existing metric based on `mainId`
              const updatedUserMetrics = userData.users[userId].filter(
                (userMetric: Metric) =>
                  String(userMetric.mainId) !== String(editMetric.id)
              );

              await updateDoc(userKpiRef, {
                [`users.${userId}`]: updatedUserMetrics,
              });
            }
          }
        }

        // ✅ 4. Assign or Update metric for new users in `userKpiMetrics`
        for (const userId of addedUsers) {
          const userKpiRef = doc(
            db,
            `organization/${organizationId}/Metrics/userKpiMetrics`
          );
          const userKpiSnap = await getDoc(userKpiRef);

          if (!userKpiSnap.exists()) {
            await setDoc(userKpiRef, { users: {} }); // ✅ Initialize users object
          }

          // ✅ Check if metric already exists (update it if it does)
          const userData = userKpiSnap.data();
          let updatedUserMetrics = userData?.users?.[userId] || [];

          console.log("update user", updatedUserMetrics);
          const metricIndex = updatedUserMetrics.findIndex(
            (m: Metric) => String(m.mainId) === String(editMetric.id)
          );
          console.log("Metric index", metricIndex);
          const { id, ...metricWithoutId } = editMetric; // Remove `id` before spreading

          console.log("edit metrice", editMetric);
          if (metricIndex !== -1) {
            // ✅ Metric exists - Update it
            updatedUserMetrics[metricIndex] = {
              ...updatedUserMetrics[metricIndex],
              ...metricWithoutId,
              updatedAt: new Date(),
            };
          } else {
            // ✅ Metric does not exist - Add new
            updatedUserMetrics.push({
              id: editMetric.id, // ✅ Keep the same metric ID
              mainId: editMetric.id, // ✅ Links back to the main KPI
              ...metricWithoutId,
              type: "common",
              createdAt: new Date(),
            });
          }

          // ✅ Update `userKpiMetrics` in Firestore
          await updateDoc(userKpiRef, {
            [`users.${userId}`]: updatedUserMetrics,
          });
        }

        // ✅ 5. Update UI State
        setMetrics((prevMetrics) =>
          prevMetrics.map((metric) =>
            metric.id === editMetric.id ? { ...editMetric } : metric
          )
        );
        showToast({ title: "Metric Updated: The metric has been successfully updated.", status: "success" });

        // Log KPI metric update activity
        try {
          const currentUserAuth = auth.currentUser;
          if (currentUserAuth && organizationId) {
            const userRef = doc(db, "users", currentUserAuth.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userData = userSnap.data();
              
              // Find original metric to track changes
              const originalMetric = metrics.find(m => m.id === editMetric.id);
              const changes: string[] = [];

              if (originalMetric) {
                if (originalMetric.metric !== editMetric.metric) changes.push(`Metric name: "${originalMetric.metric}" -> "${editMetric.metric}"`);
                if (originalMetric.description !== editMetric.description) changes.push(`Description updated`);
                if (originalMetric.target !== editMetric.target) changes.push(`Target: ${originalMetric.target} -> ${editMetric.target}`);
                if (originalMetric.status !== editMetric.status) changes.push(`Status: ${originalMetric.status} -> ${editMetric.status}`);
              }

              const changesList = changes.length > 0 ? changes.join('; ') : 'No changes';

              await logToSystem(organizationId, "actions", {
                type: "ACTIVITY",
                user: `${userData.firstName} ${userData.lastName}`,
                email: userData.email,
                action: "KPI Management",
                details: `Updated KPI metric ${editMetric.metric}. Changes: ${changesList}`,
                status: "success",
              });
            }
          }
        } catch (logError) {
          console.error("Error logging KPI metric update:", logError);
        }

        setEditableId(null);
        setEditMetric(null);
      } catch (error) {
        let errorMessage = "An unknown error occurred.";
        if (error instanceof Error) {
          errorMessage = error.message;
        }
        showToast({ title: `Error updating KPI Metric: ${errorMessage}`, status: "error" });
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleDelete = async (metricId: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this metric? This action cannot be undone."
      )
    ) {
      await confirmDelete(metricId);
    }
  };

  // ✅ Function to handle actual deletion after confirmation
  const confirmDelete = async (metricId: string) => {
    let metricToDelete: Metric | undefined;
    try {
      // ✅ 1. Delete from `commonKpiMetrics`
      const commonMetricsRef = doc(
        db,
        `organization/${organizationId}/Metrics/commonKpiMetrics`
      );
      const commonMetricsSnap = await getDoc(commonMetricsRef);

      if (commonMetricsSnap.exists()) {
        const data = commonMetricsSnap.data();

        // ✅ Find the metric and get assigned users
        metricToDelete = data.metrics.find(
          (metric: Metric) => String(metric.id) === String(metricId)
        );
        const assignedUsers = metricToDelete ? metricToDelete.assignedTo : [];

        // ✅ Remove the metric from `commonKpiMetrics`
        const updatedMetrics = data.metrics.filter(
          (metric: Metric) => String(metric.id) !== String(metricId)
        );
        await updateDoc(commonMetricsRef, { metrics: updatedMetrics });

        // ✅ 2. Remove from all assigned users in `userKpiMetrics`
        for (const userId of assignedUsers) {
          const userKpiRef = doc(
            db,
            `organization/${organizationId}/Metrics/userKpiMetrics`
          );
          const userKpiSnap = await getDoc(userKpiRef);

          if (userKpiSnap.exists()) {
            const userData = userKpiSnap.data();

            if (userData.users && userData.users[userId]) {
              // ✅ Remove the metric from user's assigned KPIs
              const updatedUserMetrics = userData.users[userId].filter(
                (userMetric: Metric) =>
                  String(userMetric.id) !== String(metricId)
              );

              await updateDoc(userKpiRef, {
                [`users.${userId}`]: updatedUserMetrics,
              });
            }
          }
        }
      }

      // ✅ 3. Update UI State
      setMetrics((prevMetrics) =>
        prevMetrics.filter((metric) => String(metric.id) !== String(metricId))
      );

      // Log KPI metric deletion activity
      try {
        const currentUserAuth = auth.currentUser;
        if (currentUserAuth && organizationId && metricToDelete) {
          const userRef = doc(db, "users", currentUserAuth.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            await logToSystem(organizationId, "actions", {
              type: "ACTIVITY",
              user: `${userData.firstName} ${userData.lastName}`,
              email: userData.email,
              action: "KPI Management",
              details: `Deleted KPI metric ${metricToDelete.metric} with target ${metricToDelete.target}`,
              status: "success",
            });
          }
        }
      } catch (logError) {
        console.error("Error logging KPI metric deletion:", logError);
      }

      showToast({ title: "Metric Deleted: The metric was successfully removed.", status: "success" });
    } catch (error) {
      showToast({ title: "Deletion Failed: Something went wrong while deleting the metric.", status: "error" });
    } finally {
    }
  };

  // Function to open the assigned users popup
  const openAssignedUsersPopup = (metric: Metric) => {
    const assignedUserDetails = metric.assignedTo.map(userId =>
      users.find(user => user.id === userId)
    ).filter((user): user is User => user !== undefined);

    setCurrentMetricUsers({
      metricName: metric.metric,
      assignedUsers: assignedUserDetails
    });
    setIsAssignedUsersPopupOpen(true);
  };

  return (
    <>
      {/* Show MetricForm if toggled */}
      {showMetricForm && (
        <MetricForm
          addMetric={(metric) =>
            setMetrics((prev) => [
              ...prev,
              { ...metric, id: Number(metric.id) },
            ])
          }
          users={users}
          color={color}
          bg={bg}
          organizationId={organizationId}
          isUserSelectOpen={false}
          setIsUserSelectOpen={() => { }}
          selectedUsers={[]}
          setSelectedUsers={() => { }}
          handleUserSelect={() => { }}
          removeAssignedUser={() => { }}
        />
      )}
      {/* Full-area overlay for Viewing Assigned Users (inside main content, not whole page) */}
      {viewingUsers !== null && (
        <div className={`flex flex-col w-full min-w-0 overflow-x-auto p-0 ${isDark ? 'bg-[#1A1C1F] text-white' : 'bg-white text-gray-900'} h-full min-h-screen absolute top-0 left-0 z-40`}>
          <div className="flex flex-col flex-1 h-full min-h-screen">
            <div className={`flex items-center justify-between px-8 py-6 border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <h3 className="font-bold text-base leading-tight">
                  Assigned Users
                </h3>
                <button
                  className={`ml-2 p-2 rounded-full ${isDark ? 'bg-[#14161B] text-[#FBD282] hover:bg-[#2A3441] focus:ring-2 focus:ring-[#B59143]' : 'bg-gray-50 text-purple-600 hover:bg-purple-50 hover:text-purple-800 focus:ring-2 focus:ring-purple-300'} transition focus:outline-none`}
                  onClick={() => setViewingUsers(null)}
                  title="Back"
                >
                  {FaArrowLeft({ size: 20 })}
                </button>
              </div>
              <button
                className="px-4 py-1.5 rounded-lg bg-purple-500 text-white font-medium hover:bg-purple-600 transition text-base ml-auto focus:outline-none focus:ring-2 focus:ring-purple-300"
                onClick={() => setViewingUsers(null)}
              >
                Done
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-6">
              {viewingUsers.length ? (
                <>
                  <ul className="space-y-2">
                    {viewingUsers.map((userId) => {
                      const user = users.find((u) => u.id === userId);
                      return user ? (
                        <li
                          key={user.id}
                          className={`flex items-center justify-between gap-3 border-l-4 ${isDark ? 'border-[#B59143] bg-[#1A1C1F] hover:bg-[#2A3441]' : 'border-purple-400 bg-white hover:bg-purple-50'} rounded-lg px-4 py-2 min-h-0 transition-all cursor-pointer`}
                        >
                          <div className="flex flex-col">
                            <span className="text-base leading-tight font-normal">
                              {user.firstName} {user.lastName}
                            </span>
                            <span className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm leading-tight`}>
                              {user.email}
                            </span>
                          </div>
                              {editMetric && (
                                <button
                                  className={`ml-2 text-red-500 hover:text-red-700 text-lg`}
                                  onClick={() => {
                                    setEditMetric((prev) => ({
                                      ...prev!,
                                      assignedTo: prev!.assignedTo.filter(
                                        (id) => id !== userId
                                      ),
                                    }));
                                    setViewingUsers(
                                      (prev) =>
                                        prev?.filter((id) => id !== userId) ||
                                        []
                                    );
                                    setSelectedUsers((prev) =>
                                      prev.filter((id) => id !== userId)
                                    );
                                  }}
                                  title="Remove"
                                >
                                  ×
                                </button>
                              )}
                            </li>
                          ) : null;
                        })}
                  </ul>
                </>
              ) : (
                <div className={`${isDark ? 'text-gray-400' : 'text-gray-400'} text-sm`}>No users assigned</div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="overflow-y-auto overflow-x-auto [scrollbar-gutter:stable]">
        {loading ? (
          <Loader message="Fetching KPI Metrics..." color="#B59143" />
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search metrics..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  className={`w-64 px-3 py-2 rounded-md text-sm outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] placeholder-[#C3C9D3] focus:ring-[#B59143] focus:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] placeholder-gray-500 focus:ring-[#B59143] focus:border-[#B59143]'}`}
                />
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {filteredMetrics.length} metrics
              </div>
            </div>
            <table className={`min-w-full divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-100'} text-sm`}>
              <thead className={`${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                <tr>
                  <th className={`px-4 py-3 text-left font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Metric
                  </th>
                  <th className={`px-4 py-3 text-left font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Description
                  </th>
                  <th className={`px-4 py-3 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Target
                  </th>
                  <th className={`px-4 py-3 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Actual
                  </th>
                  <th className={`px-4 py-3 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-4 py-3 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Assigned To
                  </th>
                  {!kpiPerms.isViewer && (
                  <th className={`px-4 py-3 text-center font-semibold tracking-wide ${isDark ? 'text-white' : 'text-gray-700'}`}>
                    Actions
                  </th>
                  )}
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'} divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-100'}`}>
                {filteredMetrics.length === 0 ? (
                  <tr>
                    <td
                      colSpan={kpiPerms.isViewer ? 6 : 7}
                      className={`text-center py-10 ${isDark ? 'text-gray-400' : 'text-gray-500'} font-normal`}
                    >
                      No metrics found.
                    </td>
                  </tr>
                ) : (
                  filteredMetrics.map((metric, idx) => {
                    const canEditAll = kpiPerms.canEdit(metric);
                    const canDeleteAll = kpiPerms.canDelete(metric);
                    const restrictionTitle = !canEditAll ? "Only the creator or an admin can edit this field" : "";

                    return (
                      <tr key={metric.id} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-gray-50'}`}>
                        {editableId === metric.id ? (
                          <>
                            <td className="px-4 py-2 text-left align-middle" title={restrictionTitle}>
                              <input
                                name="metric"
                                value={editMetric?.metric}
                                onChange={handleChange}
                                disabled={!canEditAll}
                                className={`w-full px-2 py-1 rounded text-sm text-left outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                                style={!canEditAll ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                              />
                            </td>
                            <td className="px-4 py-2 text-left align-middle" title={restrictionTitle}>
                              <input
                                name="description"
                                value={editMetric?.description || ""}
                                onChange={handleChange}
                                disabled={!canEditAll}
                                className={`w-full px-2 py-1 rounded text-sm text-left outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                                placeholder="No description provided"
                                style={!canEditAll ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                              />
                            </td>
                            <td className="px-4 py-2 text-center align-middle" title={restrictionTitle}>
                              <input
                                name="target"
                                value={editMetric?.target}
                                onChange={handleChange}
                                disabled={!canEditAll}
                                className={`w-full px-2 py-1 rounded text-sm text-center outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                                style={!canEditAll ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                              />
                            </td>
                            <td className="px-4 py-2 text-center align-middle">
                              <input
                                name="actual"
                                value={editMetric?.actual}
                                onChange={handleChange}
                                className={`w-full px-2 py-1 rounded text-sm text-center outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]' : 'bg-white text-[#111827] border border-[#E5E7EB] focus:ring-[#B59143] focus:border-[#B59143] hover:border-[#B59143]'}`}
                              />
                            </td>
                            <td className="px-4 py-2 text-center align-middle">
                              <CustomDropdown
                                value={editMetric?.status || "⚠️"}
                                onChange={(value) => {
                                  if (editMetric) {
                                    setEditMetric({
                                      ...editMetric,
                                      status: value,
                                    });
                                  }
                                }}
                                isDark={isDark}
                                className="w-full px-2 py-1 rounded text-sm text-center outline-none"
                                options={[
                                  { value: "✅", label: "✅" },
                                  { value: "⚠️", label: "⚠️" }
                                ]}
                              />
                            </td>
                            <td className="px-4 py-2 text-center align-middle">
                              <button
                                className={`text-xs font-semibold ${canEditAll ? (isDark ? 'text-[#B59143] hover:text-[#FBD282]' : 'text-[#B59143] hover:text-[#A7843D]') : 'text-gray-400 cursor-not-allowed'}`}
                                onClick={() => { if (canEditAll) setIsManageUsersOpen(true); }}
                                disabled={!canEditAll}
                                title={canEditAll ? "Manage assigned users" : "Only admins or creators can manage assignments"}
                              >
                                Manage
                              </button>
                            </td>
                            {!kpiPerms.isViewer && (
                            <td className="px-4 py-2 text-center align-middle h-full">
                              <div className="flex justify-center items-center gap-2 h-full">
                                <button
                                  className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition ${isDark ? 'bg-[#B59143] hover:bg-[#A7843D] text-white' : 'bg-[#B59143] hover:bg-[#A7843D] text-white'}`}
                                  onClick={() => handleUpdate()}
                                  title="Save changes"
                                >
                                  <IconWrapper icon={FaCheck} size={16} />
                                </button>
                                <button
                                  className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition ${isDark ? 'bg-[#B59143] hover:bg-[#A7843D] text-white' : 'bg-[#B59143] hover:bg-[#A7843D] text-white'}`}
                                  onClick={() => setEditableId(null)}
                                  title="Cancel edit"
                                >
                                  <IconWrapper icon={FaTimes} size={16} />
                                </button>
                              </div>
                            </td>
                            )}
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2 text-left align-middle">
                              {metric.metric}
                            </td>
                            <td className="px-4 py-2 text-left align-middle">
                              <span className={`${isDark ? 'text-gray-300' : ''}`}>{metric.description || "No description provided"}</span>
                            </td>
                            <td className="px-4 py-2 text-center align-middle">
                              {metric.target}
                            </td>
                            <td className="px-4 py-2 text-center align-middle">
                              {metric.actual}
                            </td>
                            <td className="px-4 py-2 text-center align-middle">
                              {metric.status}
                            </td>
                            <td className="px-4 py-2 text-center align-middle">
                              <button
                                className={`${isDark ? 'text-[#B59143] hover:text-[#FBD282]' : 'text-[#B59143] hover:text-[#A7843D]'}`}
                                onClick={() => openAssignedUsersPopup(metric)}
                                title="View assigned users"
                              >
                                <IconWrapper icon={FaEye} size={16} />
                              </button>
                            </td>
                            {!kpiPerms.isViewer && (
                            <td className="px-4 py-2 text-center align-middle h-full">
                              <div className="flex justify-center items-center gap-2 h-full">
                                <button
                                  onClick={() => handleEdit(metric)}
                                  className={`inline-flex items-center p-1 rounded-full transition-all ${isDark ? 'text-[#B59143] hover:bg-[#2A3441]' : 'text-[#B59143] hover:bg-[#FFF7E6]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                  title="Edit metric"
                                >
                                  <IconWrapper icon={CiEdit} size={16} />
                                </button>
                                {canDeleteAll && (
                                <button
                                  onClick={() => handleDelete(String(metric.id))}
                                  className={`inline-flex items-center p-1 rounded-full transition-all ${isDark ? 'text-[#B59143] hover:bg-[#2A3441]' : 'text-[#B59143] hover:bg-[#FFF7E6]'} focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                                  title="Delete metric"
                                >
                                  <IconWrapper icon={MdDeleteOutline} size={16} />
                                </button>
                                )}
                              </div>
                            </td>
                            )}
                          </>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Assigned Users Popup Modal */}
      {isAssignedUsersPopupOpen && currentMetricUsers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] text-white' : 'bg-white text-gray-900'} rounded-lg w-full max-w-sm mx-4`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <h3 className="font-bold text-base">Assigned Users for "{currentMetricUsers.metricName}"</h3>
              <button
                className={`${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700'} text-base`}
                onClick={() => setIsAssignedUsersPopupOpen(false)}
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 max-h-[40vh] overflow-y-auto">
              {currentMetricUsers.assignedUsers.length > 0 ? (
                <ul className="space-y-2">
                  {currentMetricUsers.assignedUsers.map((user) => (
                    <li key={user.id} className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">{user.firstName} {user.lastName}</span>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No users assigned.</p>
              )}
            </div>
            <div className={`flex justify-end px-4 py-3 border-t ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <Button variant="gold" onClick={() => setIsAssignedUsersPopupOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Assigned Users (Edit Mode) */}
      {isManageUsersOpen && editMetric && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] text-white' : 'bg-white text-gray-900'} rounded-lg w-full max-w-md mx-4`}>
            <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <h3 className="font-bold text-base">Manage Users for "{editMetric.metric}"</h3>
              <button
                className={`${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-700'} text-base`}
                onClick={() => setIsManageUsersOpen(false)}
                title="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <input
                type="text"
                placeholder="Search users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className={`w-full px-3 py-2 rounded-md text-sm outline-none ${isDark ? 'bg-[#0B0B0F] text-white border border-[#2E2F33] focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143]' : 'bg-white text-[#111827] border border-gray-200 focus:ring-[#B59143] focus:border-[#B59143]'}`}
              />
              <div className="max-h-[45vh] overflow-y-auto">
                <ul className="space-y-2">
                  {users
                    .filter((u) =>
                      `${u.firstName} ${u.lastName} ${u.email}`
                        .toLowerCase()
                        .includes(userSearch.toLowerCase())
                    )
                    .map((user) => (
                      <li key={user.id} className={`flex items-center justify-between px-3 py-2 rounded border ${isDark ? 'hover:bg-[#2A3441] border-[#2E2F33]' : 'hover:bg-gray-50 border-gray-100'}`}>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{user.firstName} {user.lastName}</span>
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</span>
                        </div>
                        <input
                          type="checkbox"
                          className="h-5 w-5 accent-[#B59143]"
                          checked={editMetric.assignedTo.includes(user.id)}
                          onChange={() => handleUserSelection(user.id)}
                        />
                      </li>
                    ))}
                </ul>
              </div>
            </div>
            <div className={`flex justify-end px-4 py-3 border-t ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
              <Button variant="gold" onClick={() => setIsManageUsersOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog
        isOpen={isPermissionModalOpen}
        leastDestructiveRef={permissionCancelRef}
        onClose={() => setPermissionModalOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Restricted Access
            </AlertDialogHeader>
            <AlertDialogBody>
              You can only edit KPI metrics created by you. This metric was created by another user.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={permissionCancelRef} onClick={() => setPermissionModalOpen(false)}>
                OK
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default MetricTable;
